/**
 * GoogleMap Component
 *
 * Property map using Google Maps API.
 * Displays property location with tier-colored marker.
 * Clean, modern styling with minimal UI.
 */

import { useCallback, useState, memo } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { Loader2 } from 'lucide-react';
import { useGoogleMaps } from '../../providers/GoogleMapsProvider';

export interface PropertyMarker {
  id: string;
  lat: number;
  lng: number;
  address: string;
  tier?: 'Tier1' | 'Tier2' | 'Tier3';
  price?: number;
}

export interface PropertyGoogleMapProps {
  /** Center coordinates */
  center: { lat: number; lng: number };
  /** Zoom level (default: 15) */
  zoom?: number;
  /** Main property marker */
  property?: PropertyMarker;
  /** Comparable property markers */
  comparables?: PropertyMarker[];
  /** Height of map container */
  height?: string;
}

/**
 * Modern map styles - clean and minimal
 */
const modernMapStyle: google.maps.MapTypeStyle[] = [
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c9e4f0' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e0e0e0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#dadada' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#d4edda' }],
  },
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }],
  },
];

/**
 * Get marker color based on tier
 */
function getTierMarkerColor(tier?: string): string {
  switch (tier) {
    case 'Tier1':
      return '#10b981'; // Green
    case 'Tier2':
      return '#f59e0b'; // Amber
    case 'Tier3':
      return '#ef4444'; // Red
    default:
      return '#3b82f6'; // Blue
  }
}

/**
 * Create SVG marker icon for tier
 */
function createMarkerIcon(tier?: string, isMain: boolean = false): string {
  const color = getTierMarkerColor(tier);
  const size = isMain ? 40 : 30;
  const stroke = isMain ? 3 : 2;

  // SVG marker with pin shape
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
      <path fill="${color}" stroke="#ffffff" stroke-width="${stroke}"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle fill="#ffffff" cx="12" cy="9" r="3"/>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * Loading placeholder
 */
function MapLoading() {
  return (
    <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg" style={{ height: '400px' }}>
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span>Loading map...</span>
      </div>
    </div>
  );
}

/**
 * Error/No API key placeholder
 */
function MapPlaceholder({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-8" style={{ height: '400px' }}>
      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
      <p className="text-gray-500 dark:text-gray-400 text-center text-sm">{message}</p>
    </div>
  );
}

/**
 * Format price for display
 */
function formatPrice(price?: number): string {
  if (!price) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
}

function PropertyGoogleMapInner({
  center,
  zoom = 15,
  property,
  comparables = [],
  height = '400px',
}: PropertyGoogleMapProps) {
  const [selectedMarker, setSelectedMarker] = useState<PropertyMarker | null>(null);

  const onMapLoad = useCallback((_map: google.maps.Map) => {
    // Map loaded - could add additional setup here
  }, []);

  const mapOptions: google.maps.MapOptions = {
    styles: modernMapStyle,
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: true,
    fullscreenControl: true,
    gestureHandling: 'cooperative',
  };

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height, borderRadius: '8px' }}
      center={center}
      zoom={zoom}
      options={mapOptions}
      onLoad={onMapLoad}
    >
      {/* Main property marker */}
      {property && (
        <Marker
          position={{ lat: property.lat, lng: property.lng }}
          icon={{
            url: createMarkerIcon(property.tier, true),
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 40),
          }}
          onClick={() => setSelectedMarker(property)}
          zIndex={1000}
        />
      )}

      {/* Comparable markers */}
      {comparables.map((comp) => (
        <Marker
          key={comp.id}
          position={{ lat: comp.lat, lng: comp.lng }}
          icon={{
            url: createMarkerIcon(comp.tier, false),
            scaledSize: new google.maps.Size(30, 30),
            anchor: new google.maps.Point(15, 30),
          }}
          onClick={() => setSelectedMarker(comp)}
        />
      ))}

      {/* Info window for selected marker */}
      {selectedMarker && (
        <InfoWindow
          position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          onCloseClick={() => setSelectedMarker(null)}
          options={{ pixelOffset: new google.maps.Size(0, -40) }}
        >
          <div className="p-2 min-w-[150px]">
            <p className="font-semibold text-gray-900 text-sm">{selectedMarker.address}</p>
            {selectedMarker.tier && (
              <p className="text-xs text-gray-600 mt-1">{selectedMarker.tier}</p>
            )}
            {selectedMarker.price && (
              <p className="text-sm font-medium text-blue-600 mt-1">{formatPrice(selectedMarker.price)}</p>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
}

/**
 * Main exported component using shared Google Maps provider
 */
export function PropertyGoogleMap(props: PropertyGoogleMapProps) {
  const { isLoaded, loadError } = useGoogleMaps();

  // Handle loading/error states
  if (loadError) {
    return (
      <MapPlaceholder message="Google Maps API key not configured. Add VITE_GOOGLE_MAPS_API_KEY to .env file." />
    );
  }

  if (!isLoaded) {
    return <MapLoading />;
  }

  // Check if we have valid coordinates
  if (!props.center || typeof props.center.lat !== 'number' || typeof props.center.lng !== 'number') {
    return (
      <MapPlaceholder message="Property coordinates not available." />
    );
  }

  return <PropertyGoogleMapInner {...props} />;
}

export default memo(PropertyGoogleMap);
